import { useState } from 'react';
import { Card, Form, Typography, Button, Statistic, Col, InputNumber, Select, Descriptions, Tag, Table } from 'antd';
import { users } from '../../../../../../../API/TT_ELD/Users/user';
import { companies } from '../../../../../../../API/TT_ELD/Users/company';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useCompanyData, useFreeTrialHistory } from '../../../../../../../Hooks/TT_ELD/Companies';
import { getUserFromLocalStorage } from '../../../../../../../Utils/sidebar';
import moment from 'moment';

const { Timer } = Statistic;
const { Text } = Typography;

type MyObjectType = { [key: string | number]: any };
type params = { readonly id: any };
interface FreeTrialResponse { data: any; error: string }

const CAN_SUBMIT_ROLES = ['support_manager', 'manager', 'sales'];
const EXTENDED_LIMIT_ROLES = ['manager', 'sales'];
const HISTORY_PAGE_SIZE = 10;

const formatDate = (date?: string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

const historyColumns = [
  {
    title: 'No',
    dataIndex: 'no',
    key: 'no',
    width: 60,
  },
  {
    title: 'Opened Date',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: formatDate,
  },
  {
    title: 'From',
    dataIndex: 'startDate',
    key: 'startDate',
    render: formatDate,
  },
  {
    title: 'To',
    dataIndex: 'endDate',
    key: 'endDate',
    render: formatDate,
  },
  {
    title: 'Range',
    dataIndex: 'days',
    key: 'days',
    render: (days: number | null) =>
      days !== null && days !== undefined ? `${days} day${days === 1 ? '' : 's'}` : '-',
  },
  {
    title: 'Opened By',
    dataIndex: 'createdBy',
    key: 'createdBy',
    render: (createdBy: any) => {
      if (!createdBy) return '-';
      const fullName = [createdBy.first_name, createdBy.second_name].filter(Boolean).join(' ');
      return (
        <div>
          <div>{fullName || '-'}</div>
          {createdBy.email && <Text type="secondary">{createdBy.email}</Text>}
        </div>
      );
    },
  },
  {
    title: 'Role',
    dataIndex: 'createdBy',
    key: 'role',
    render: (createdBy: any) => (createdBy?.admin_type ? <Tag>{createdBy.admin_type}</Tag> : '-'),
  },
];

const FreeTrial = () => {
  const [form] = Form.useForm();
  const { id } = useParams<params>();
  const { data, isFetching, isLoading, refetch }: MyObjectType = useCompanyData(id);
  const currentRole = getUserFromLocalStorage()?.role ?? '';
  const canSubmit = CAN_SUBMIT_ROLES.includes(currentRole);
  const maxLimitDays = EXTENDED_LIMIT_ROLES.includes(currentRole) ? 30 : 10;

  const freeTrial = data?.freeTrial;
  const isFreeTrialActive = freeTrial?.status === 'active';
  const freeTrialEndDate = freeTrial?.endDate ? new Date(freeTrial.endDate) : undefined;
  const isActivated = !!freeTrial;

  const [historyPage, setHistoryPage] = useState(1);
  const history = useFreeTrialHistory(id, historyPage);
  const historyDataSource = history.data?.data?.map((row: any, index: number) => ({
    ...row,
    key: row.id ?? index,
    no: (historyPage - 1) * HISTORY_PAGE_SIZE + index + 1,
  }));

  const onSaved = () => {
    refetch();
    history.refetch();
  };

  const onPost = useMutation(
    'freeTrial',
    ({ values, id }: { values: string | number; id: string | number }) =>
      users.freeTrial(values, id),
    { onSuccess: onSaved },
  );

  const onPatch = useMutation(
    ({ freeTrialId, quantity }: { freeTrialId: number | string; quantity: number }) =>
      companies.updateFreeTrial(freeTrialId, quantity),
    { onSuccess: onSaved },
  );

  const onFinish = async (values: any) => {
    if (freeTrial?.id) {
      await onPatch.mutate({ freeTrialId: id, quantity: values.quantity });
    } else {
      await onPost.mutate({ values, id });
    }
  };

  const isSaving = onPost.isLoading || onPatch.isLoading;

  type FieldType = { limit?: number; quantity?: string };

  return (
    <div className="mainPage">
      <Card loading={isLoading || isFetching} title="Free trial" style={{ width: '100%' }}>

        {/* ── Countdown ── */}
        {isFreeTrialActive && freeTrialEndDate && (
          <Col style={{ marginBottom: 32 }}>
            <Timer
              type="countdown"
              valueStyle={{ fontSize: '32px' }}
              title="Free trial ends"
              value={freeTrialEndDate.getTime()}
              format=" DD : HH : mm : ss "
            />
            <Text type="secondary" style={{ margin: '0 22px 0 6px' }}>Days</Text>
            <Text type="secondary" style={{ margin: '0 10px 0 2px' }}>Hours</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Minutes</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Seconds</Text>
          </Col>
        )}

        {/* ── Free trial info ── */}
        {freeTrial && (
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Status">
              <Tag color={freeTrial.status === 'active' ? 'green' : 'default'}>
                {freeTrial.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Outcome">
              <Tag color={
                freeTrial.outcome === 'converted' ? 'blue'
                : freeTrial.outcome === 'cancelled' ? 'red'
                : 'orange'
              }>
                {freeTrial.outcome ?? '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Days">{freeTrial.days ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Quantity">{freeTrial.quantity ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {freeTrial.startDate ? moment(freeTrial.startDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {freeTrial.endDate ? moment(freeTrial.endDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}

        {/* ── Form ── */}
        <Form
          form={form}
          layout="vertical"
          style={{ maxWidth: 600 }}
          onFinish={onFinish}
          name="basic"
          initialValues={{
            limit: 7,
            quantity: freeTrial?.quantity ?? data?.quantity,
          }}
        >
          {!isActivated && (
            <Form.Item<FieldType>
              label="Limit"
              name="limit"
              rules={[{ required: true, message: 'Please input your limit!' }]}
            >
              <Select style={{ width: '100%' }}>
                {Array.from({ length: maxLimitDays }, (_, i) => (
                  <Select.Option key={i + 1} value={i + 1}>
                    {i + 1} day{i + 1 > 1 ? 's' : ''}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item<FieldType>
            label="Quantity"
            name="quantity"
            rules={[{ required: true, message: 'Please input your quantity!' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          {canSubmit && (
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isSaving}>
                Submit
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>

      <Card title="Free trial history" style={{ width: '100%', marginTop: 16 }}>
        <Table
          dataSource={historyDataSource}
          columns={historyColumns}
          loading={history.isLoading || history.isFetching}
          scroll={{ x: 'max-content' }}
          pagination={{
            total: history.data?.count,
            current: historyPage,
            pageSize: HISTORY_PAGE_SIZE,
            showSizeChanger: false,
            onChange: setHistoryPage,
          }}
        />
      </Card>
    </div>
  );
};

export default FreeTrial;
